import { Suspense, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { cn } from 'src/shared/utils/cn'

import { usePortfolioItems } from '../../../api/portfolio/use-portfolio-items'
import NoPortfolio from '../empty/no-portfolio'
import SectionTitle from '../ui/section-title'
import ViewAllButton from '../ui/view-all-button'

import PortfolioCard from './portfolio-card'
import PortfolioListSkeleton from './portfolio-list-skeleton'

type PortfolioListProps = {
  username: string
  profession: string
  onGoBack?: () => void
  showAll?: boolean
  mobile?: boolean
  userId: number
  firstName: string
}

const PortfolioListContent = ({
  userId,
  username,
  profession,
  onGoBack,
  showAll = false,
  mobile,
  firstName,
}: PortfolioListProps) => {
  const navigate = useNavigate()
  const isRealtor = profession === 'real_estate_agent'
  const { data: portfolioData } = usePortfolioItems(userId)

  const portfolio = useMemo(() => {
    if (showAll) {
      return portfolioData
    }

    return portfolioData.slice(0, 4)
  }, [portfolioData, showAll])

  return (
    <section className="mb-6">
      {(isRealtor || !mobile) && (
        <SectionTitle
          onGoBack={onGoBack}
          title={'Portfolio'}
          rightContent={
            mobile ? null : (
              <span
                className={cn(
                  'text-sm font-semibold text-muted lg:text-base',
                  portfolio.length === 0 && 'italic text-muted',
                )}
              >
                {portfolioData.length} item{portfolioData.length === 1 ? '' : 's'}
              </span>
            )
          }
        />
      )}
      <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(350px,1fr))] gap-4">
        {portfolio.map((item) => (
          <PortfolioCard key={item.id} item={item} username={username} isRealtor={isRealtor} />
        ))}
      </div>
      {portfolio.length === 0 && <NoPortfolio firstName={firstName} />}
      {!showAll && portfolio.length > 0 && (
        <ViewAllButton
          text="Show all items"
          onClick={() => navigate(paths.users.publicProfile.portfolio.getHref(username))}
        />
      )}
    </section>
  )
}

const PortfolioList = ({
  userId,
  username,
  profession,
  onGoBack,
  showAll = false,
  mobile,
  firstName,
}: PortfolioListProps) => {
  return (
    <Suspense fallback={<PortfolioListSkeleton />}>
      <PortfolioListContent
        username={username}
        profession={profession}
        onGoBack={onGoBack}
        showAll={showAll}
        mobile={mobile}
        userId={userId}
        firstName={firstName}
      />
    </Suspense>
  )
}

export default PortfolioList
